import { AUTHORITIES } from "@/config/constants";
import { useAppSelector } from "@/config/store";
import { hasAnyAuthority } from "@/shared/auth/private-route";
import { IEdoChapter } from "@/shared/model/edo-chapter.model";
import { IEdoUnit } from "@/shared/model/edo-unit.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { Button, Card, IconButton, List } from "@/components/ui";
import { Plus, RefreshCw } from "lucide-react";
import axios from "axios";
import "moment/locale/it";
import { useEffect, useMemo, useState } from "react";
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from "react-beautiful-dnd";
import { Translate } from "react-jhipster";
import { UnitRow } from "../CapitoloRow/UnitRow";

interface UnitsProps {
  chapter?: IEdoChapter;
  handleOpenViewUnitDialog: (unit: IEdoUnit) => void;
  handleOpenEditUnitDialog: (unit: IEdoUnit | null) => void;
  handleOpenDeleteUnitDialog: (unit: IEdoUnit) => void;
}

const Units = (props: UnitsProps) => {
  const [units, setUnits] = useState<IEdoUnit[]>(props.chapter?.units ?? []);
  const [loadingUnits, setLoadingUnits] = useState<boolean>(false);
  const [unitsLocal, setUnitsLocal] = useState<IEdoUnit[]>(units);
  const { account } = useAppSelector<AuthenticationState>(
    (state) => state.authentication
  );
  const { updateSuccess } = useAppSelector<EntityState<IEdoUnit>>(
    (state) => state.edoUnit
  );

  const {
    chapter,
    handleOpenViewUnitDialog,
    handleOpenEditUnitDialog,
    handleOpenDeleteUnitDialog,
  } = props;

  const getEdoUnitList = async () => {
    if (!chapter?.id) return;
    setLoadingUnits(true);
    const response = await axios.get("/units", {
      params: {
        page: 0,
        limit: 100,
        sortBy: "order:ASC",
        "filter.chapter.id": "$eq:" + chapter.id,
      },
    });
    setUnits(response.data.data);
    setLoadingUnits(false);
  };

  useEffect(() => {
    if (chapter) {
      getEdoUnitList();
    }
  }, [chapter]);

  useEffect(() => {
    setUnitsLocal(units);
  }, [units]);

  useEffect(() => {
    if (updateSuccess) {
      getEdoUnitList();
    }
  }, [updateSuccess]);

  const handleSyncList = () => {
    getEdoUnitList();
  };

  const handleOnDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    const items = Array.from(unitsLocal);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setUnitsLocal(items);
    try {
      await axios.patch(`/units/${result.draggableId}/order`, {
        order: result.destination.index + 1,
      });
    } catch (e) {
      getEdoUnitList();
    }
  };

  const isAdmin = useMemo(
    () =>
      hasAnyAuthority(account?.roles ?? [], [
        AUTHORITIES.ROLE_ADMIN,
        AUTHORITIES.ROLE_VICE,
        AUTHORITIES.ROLE_TEACHER,
      ]),
    [account?.roles]
  );

  return (
    <Card className="surface overflow-hidden rounded-3xl p-0">
      <div className="flex flex-col gap-2 border-b border-[var(--color-mockup-border)] bg-[var(--color-surface-secondary)]/50 px-4 py-3 dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]/30 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-heading font-semibold text-[var(--color-mockup-text)]">
          <Translate contentKey="edocendoApp.edoUnit.home.title">
            Edo Unit
          </Translate>
        </h3>
        <div className="flex items-center gap-2">
          <IconButton
            onClick={handleSyncList}
            disabled={loadingUnits}
            aria-label="Aggiorna lista"
            className="sm:hidden"
          >
            <RefreshCw className="h-4 w-4" />
          </IconButton>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSyncList}
            disabled={loadingUnits}
            className="hidden sm:inline-flex"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            <Translate contentKey="edocendoApp.edoUnit.home.refreshListLabel">
              Refresh List
            </Translate>
          </Button>
          {isAdmin && (
            <>
              <IconButton
                disabled={loadingUnits}
                onClick={() => handleOpenEditUnitDialog(null)}
                aria-label="Aggiungi unità"
                className="sm:hidden"
              >
                <Plus className="h-4 w-4" />
              </IconButton>
              <Button
                variant="outline"
                size="sm"
                disabled={loadingUnits}
                onClick={() => handleOpenEditUnitDialog(null)}
                className="hidden sm:inline-flex"
              >
                <Plus className="mr-2 h-4 w-4" />
                Aggiungi unità
              </Button>
            </>
          )}
        </div>
      </div>

      {loadingUnits ? (
        <div className="flex justify-center py-8">
          <span className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : !units?.length ? (
        <div className="p-6 text-center">
          <p className="text-body text-[var(--color-mockup-text-mid)]">
            <Translate contentKey="edocendoApp.edoUnit.home.notFound">
              No Edo Units found
            </Translate>
          </p>
        </div>
      ) : (
        <List>
          <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="materiale" isDropDisabled={!isAdmin}>
              {(provided) => (
                <div
                  className="materiale"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {unitsLocal.map((unit, index) => (
                    <Draggable
                      key={unit.id}
                      draggableId={String(unit.id)}
                      index={index}
                      isDragDisabled={!isAdmin}
                    >
                      {(providedEl) => (
                        <div
                          ref={providedEl.innerRef}
                          {...providedEl.draggableProps}
                          {...providedEl.dragHandleProps}
                        >
                          <UnitRow
                            unit={unit}
                            viewUnit={() => handleOpenViewUnitDialog(unit)}
                            editUnit={() => handleOpenEditUnitDialog(unit)}
                            deleteUnit={() =>
                              handleOpenDeleteUnitDialog(unit)
                            }
                            nested={false}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </List>
      )}
    </Card>
  );
};

export default Units;
